import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  CircularProgress,
} from '@mui/material'
import Business from '@mui/icons-material/Business'
import LocationOn from '@mui/icons-material/LocationOn'
import MedicalServices from '@mui/icons-material/MedicalServices'
import ArrowForward from '@mui/icons-material/ArrowForward'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router'
import { ClinicAppointmentTypesListing } from 'src/clinic/views/clinicServices/ClinicAppointmentTypes'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import useServerAuth from 'src/customHooks/useServerAuth'

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: 'calc(100vh - 200px)',
  background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}))

const SelectionCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}))

const StyledSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
  },
}))

const ContinueButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
  color: '#ffffff',
  borderRadius: '12px',
  padding: theme.spacing(1.5, 4),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(17, 39, 71, 0.25)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(17, 39, 71, 1) 0%, rgba(30, 58, 138, 1) 100%)',
    boxShadow: '0 6px 16px rgba(17, 39, 71, 0.35)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    opacity: 0.6,
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}))

const ServicesContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  borderRadius: '16px',
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  border: '1px solid rgba(0, 0, 0, 0.08)',
}))

export default function SupportClinicServicesDirectEntry() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { allRolesInBoolean } = useServerAuth()
  const [clinicBrands, setClinicBrands] = useState([])
  const [clinicLocations, setClinicLocations] = useState([])
  const [selectedBrandId, setSelectedBrandId] = useState('')
  const [selectedLocationId, setSelectedLocationId] = useState('')
  const [selectedBrand, setSelectedBrand] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showServices, setShowServices] = useState(false)
  const [managerAssignedBrandIds, setManagerAssignedBrandIds] = useState([])
  const [managerAssignedLocationIds, setManagerAssignedLocationIds] = useState([])

  // Check if user is clinic success admin or manager
  const isClinicSuccessAdmin = allRolesInBoolean.isClinicSuccessAdmin
  const isClinicSuccessManager = allRolesInBoolean.isClinicSuccessManager
  const hasAccess = isClinicSuccessAdmin || isClinicSuccessManager

  async function getManagerProfile() {
    if (!isClinicSuccessManager) return

    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/clinic-success-manager/profile',
        {},
        undefined,
        undefined,
        true,
      )
      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)
        // Extract assigned brand IDs and location IDs from manager profile
        // The API might return data in different formats, so we check multiple possibilities
        const assignedBrands = data?.['clinic-brand'] || data?.clinicBrands || data?.brands || []
        const assignedLocations = data?.['clinic'] || data?.clinicLocations || data?.locations || []
        
        // Extract brand IDs - handle different possible structures
        const brandIds = assignedBrands.map((brand) => 
          brand?.id || brand?.clinicBrandId || brand?.brandId
        ).filter(Boolean)
        
        // Extract location IDs - handle different possible structures
        const locationIds = assignedLocations.map((loc) => 
          loc?.id || loc?.clinicLocationId || loc?.locationId
        ).filter(Boolean)
        
        setManagerAssignedBrandIds(brandIds)
        setManagerAssignedLocationIds(locationIds)
      }
    } catch (error) {
      console.error('Error fetching manager profile:', error)
      // If API fails, manager won't see any brands/locations (empty arrays)
      setManagerAssignedBrandIds([])
      setManagerAssignedLocationIds([])
    }
  }

  async function getClinicBrands() {
    const resp = await getSPDataWithParams(
      '/api/secure/cc/support/clinic-brand/list',
      {},
      undefined,
      undefined,
      true,
    )
    if (resp && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)?.['clinic-brand']
      setClinicBrands(data || [])
    }
  }

  async function getClinicLocations() {
    const resp = await getSPDataWithParams(
      '/api/secure/cc/support/clinic/list',
      {},
      undefined,
      undefined,
      true,
    )
    if (resp && isCommonResponseSuccessfulV2(resp)) {
      const data = getDataMapFromCommonResponse(resp)?.['clinic']
      setClinicLocations(data || [])
    }
  }

  async function Setup() {
    setIsLoading(true)
    await Promise.all([getManagerProfile(), getClinicBrands(), getClinicLocations()])
    setIsLoading(false)
  }

  useEffect(() => {
    Setup()
  }, [])

  // Filter brands based on manager's assigned brands (if manager) or show all (if admin)
  const availableBrands = useMemo(() => {
    if (isClinicSuccessAdmin) {
      return clinicBrands
    }
    if (isClinicSuccessManager && managerAssignedBrandIds.length > 0) {
      return clinicBrands.filter((brand) => managerAssignedBrandIds.includes(brand.id))
    }
    return []
  }, [clinicBrands, managerAssignedBrandIds, isClinicSuccessAdmin, isClinicSuccessManager])

  // Filter locations by selected brand and manager's assigned locations (if manager)
  const filteredLocations = useMemo(() => {
    if (!selectedBrandId) return []
    let locations = clinicLocations.filter((location) => location?.clinicBrandId === selectedBrandId)
    
    // If manager, further filter by assigned locations
    if (isClinicSuccessManager && managerAssignedLocationIds.length > 0) {
      locations = locations.filter((location) => managerAssignedLocationIds.includes(location.id))
    }
    
    return locations
  }, [clinicLocations, selectedBrandId, managerAssignedLocationIds, isClinicSuccessManager])

  const handleBrandChange = (event) => {
    const brandId = event.target.value
    setSelectedBrandId(brandId)
    setSelectedLocationId('')
    setSelectedLocation(null)
    setSelectedBrand(availableBrands.find((b) => b.id === brandId) || null)
    setShowServices(false)
  }

  const handleLocationChange = (event) => {
    const locationId = event.target.value
    setSelectedLocationId(locationId)
    setSelectedLocation(filteredLocations.find((l) => l.id === locationId) || null)
    setShowServices(false)
  }

  const handleContinue = () => {
    if (selectedBrandId && selectedLocationId) {
      setShowServices(true)
    }
  }

  const canContinue = selectedBrandId && selectedLocationId

  if (!hasAccess) {
    return (
      <>
        <SupportHeader heading="Clinic Services" showBackButton />
        <Container>
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="error">
              Access Denied
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              This page is only available to Clinic Success Admins and Managers.
            </Typography>
          </Card>
        </Container>
      </>
    )
  }

  return (
    <>
      <SupportHeader heading="Clinic Services - Direct Entry" showBackButton />
      <Container>
        {isLoading ? (
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
          </Stack>
        ) : (
          <>
            <SelectionCard>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Select Clinic Brand & Location
                </Typography>
                <Stack spacing={3}>
                  <StyledSelect fullWidth>
                    <InputLabel id="clinic-brand-select-label">Clinic Brand</InputLabel>
                    <Select
                      labelId="clinic-brand-select-label"
                      id="clinic-brand-select"
                      value={selectedBrandId}
                      label="Clinic Brand"
                      onChange={handleBrandChange}
                    >
                      {availableBrands.length === 0 && !isLoading ? (
                        <MenuItem disabled>
                          <Typography variant="body2" color="text.secondary">
                            No clinic brands available
                          </Typography>
                        </MenuItem>
                      ) : (
                        availableBrands.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id}>
                            {brand.brandName || brand.title || brand.name || 'Unknown Brand'}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </StyledSelect>

                  <StyledSelect fullWidth disabled={!selectedBrandId}>
                    <InputLabel id="clinic-location-select-label">Clinic Location</InputLabel>
                    <Select
                      labelId="clinic-location-select-label"
                      id="clinic-location-select"
                      value={selectedLocationId}
                      label="Clinic Location"
                      onChange={handleLocationChange}
                    >
                      {filteredLocations.length === 0 && selectedBrandId ? (
                        <MenuItem disabled>
                          <Typography variant="body2" color="text.secondary">
                            No locations available for this brand
                          </Typography>
                        </MenuItem>
                      ) : (
                        filteredLocations.map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.title || location.name || 'Unknown Location'}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </StyledSelect>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                    <ContinueButton
                      onClick={handleContinue}
                      disabled={!canContinue}
                      endIcon={<ArrowForward />}
                    >
                      Continue to Services
                    </ContinueButton>
                  </Box>
                </Stack>
              </CardContent>
            </SelectionCard>

            {showServices && selectedBrand && selectedLocation && (
              <ServicesContainer>
                <Box
                  sx={{
                    p: 3,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.02) 0%, rgba(30, 58, 138, 0.01) 100%)',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Business sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedBrand.brandName || selectedBrand.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedLocation.title || selectedLocation.name}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  <ClinicAppointmentTypesListing
                    getDefaultServicesApiSignature="/api/secure/cc/support/clinic/default-templates/list"
                    getServicesApiSignature="/api/secure/cc/support/clinic/clinic-appointment-type/list"
                    getParamsForClinicServices={() => ({
                      'clinic-location-id': selectedLocation.id,
                      'clinic-brand-id': selectedBrand.id,
                      page: 1,
                      'page-size': 500,
                    })}
                    clinicId={selectedLocation.id}
                    clinicBrandId={selectedBrand.id}
                    clinicLocation={selectedLocation}
                    saveAppointmentApiSignature="/api/secure/cc/support/clinic/clinic-appointment-type/save"
                  />
                </Box>
              </ServicesContainer>
            )}
          </>
        )}
      </Container>
    </>
  )
}

